/**
 * Documents Page
 * Displays AI-generated artifacts saved from chat conversations
 */

import { useState, useEffect, useMemo } from "react";
import {
  MdInsertChart,
  MdTableChart,
  MdDeleteOutline,
  MdDownload,
  MdVisibility,
  MdAutoAwesome,
} from "react-icons/md";
import { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import DataTable from "components/data-table";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { ArtifactChart, ArtifactTable, exportArtifact } from "components/ai-chat/artifacts";
import notify from "lib/notify";
import documentsService from "services/documentsService";
import { EMPTY, formatDateTime } from "utils/format";

const TYPE_META = {
  chart: { label: "Chart", icon: MdInsertChart, tone: "brand" },
  table: { label: "Table", icon: MdTableChart, tone: "info" },
};

const DocumentsPage = () => {
  const [artifacts, setArtifacts] = useState([]);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadingDataId, setLoadingDataId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await documentsService.listDocuments();
      if (response.success) {
        setArtifacts(response.data);
      }
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  };

  // Uses documentData from the backend when present (avoids S3 CORS issues).
  const fetchArtifactData = async (id) => {
    const response = await documentsService.getDocument(id);
    if (!response.success) return undefined;
    let data = response.data.documentData;
    if (!data && response.data.dataUrl) {
      data = await documentsService.fetchDocumentData(response.data.dataUrl);
    }
    return data;
  };

  const handleDelete = async (artifact) => {
    const confirmed = await confirm({
      title: `Delete "${artifact.title || "Untitled"}"?`,
      message: "This document will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await documentsService.deleteDocument(artifact.id);
      setArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
      notify.success("Document deleted");
    } catch (e) {
      notify.error(getApiErrorMessage(e, "Could not delete document"));
    }
  };

  const handleView = async (doc) => {
    setLoadingDataId(doc.id);
    try {
      const data = await fetchArtifactData(doc.id);
      if (data !== undefined) setSelectedArtifact({ ...doc, data });
    } catch (e) {
      notify.error(getApiErrorMessage(e, "Could not load document"));
    } finally {
      setLoadingDataId(null);
    }
  };

  const handleExport = async (artifact) => {
    let data = artifact.data;

    // Load data on demand if not already loaded
    if (!data) {
      try {
        data = await fetchArtifactData(artifact.id);
      } catch (e) {
        notify.error(getApiErrorMessage(e, "Could not load document data for export"));
        return;
      }
    }

    exportArtifact(artifact.type, data);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => {
          const meta = TYPE_META[row.original.type] || TYPE_META.table;
          const Icon = meta.icon;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-surface-subtle text-fg-muted"
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="truncate font-semibold text-fg">{row.original.title || "Untitled"}</span>
            </div>
          );
        },
      },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const meta = TYPE_META[row.original.type] || TYPE_META.table;
          return <Badge tone={meta.tone}>{meta.label}</Badge>;
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        meta: { className: "whitespace-nowrap" },
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: "creator",
        header: "Created By",
        cell: ({ row }) => {
          const creator = row.original.creator;
          return creator ? `${creator.firstName} ${creator.lastName}` : EMPTY;
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const artifact = row.original;
          const title = artifact.title || "Untitled";
          return (
            <div className="flex items-center justify-end gap-1">
              <IconButton
                icon={<MdVisibility />}
                label={`View ${title}`}
                size="sm"
                loading={loadingDataId === artifact.id}
                disabled={loadingDataId !== null}
                onClick={() => handleView(artifact)}
              />
              <IconButton
                icon={<MdDownload />}
                label={`Export ${title}`}
                size="sm"
                onClick={() => handleExport(artifact)}
              />
              <IconButton
                icon={<MdDeleteOutline />}
                label={`Delete ${title}`}
                size="sm"
                onClick={() => handleDelete(artifact)}
              />
            </div>
          );
        },
      },
    ],
    // Handlers only close over state setters and services.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingDataId]
  );

  const selectedTitle =
    selectedArtifact?.data?.title || selectedArtifact?.title || "Artifact";

  return (
    <PageContainer>
      <PageHeader
        title="Documents"
        description="AI-generated charts and reports from your conversations."
      />

      {loadError ? (
        <ErrorState
          message={getApiErrorMessage(loadError, "Could not load documents.")}
          onRetry={loadDocuments}
        />
      ) : !loading && artifacts.length === 0 ? (
        <EmptyState
          icon={MdAutoAwesome}
          title="No Documents Yet"
          description='Charts and reports generated by Atrium AI appear here. Select "Save" on any chart or table in the chat to add it.'
        />
      ) : (
        <DataTable
          aria-label="Documents"
          columns={columns}
          data={artifacts}
          loading={loading}
          getRowId={(a) => String(a.id)}
          onRowClick={(artifact) => {
            if (loadingDataId === null) handleView(artifact);
          }}
        />
      )}

      <Dialog
        isOpen={Boolean(selectedArtifact)}
        onClose={() => setSelectedArtifact(null)}
        size="xl"
        title={selectedTitle}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelectedArtifact(null)}>
              Close
            </Button>
            <Button leftIcon={<MdDownload />} onClick={() => handleExport(selectedArtifact)}>
              Export
            </Button>
          </>
        }
      >
        {selectedArtifact?.type === "chart" && selectedArtifact.data && (
          <ArtifactChart
            id={`doc-${selectedArtifact.id}`}
            data={selectedArtifact.data}
            height={400}
          />
        )}
        {selectedArtifact?.type === "table" && selectedArtifact.data && (
          <ArtifactTable data={selectedArtifact.data} aria-label={selectedTitle} />
        )}
      </Dialog>
    </PageContainer>
  );
};

export default DocumentsPage;
